// Step kinds, registered by name. Open by design: each product owns its own
// kinds (namespace them, e.g. "lona.plan-options"); built-ins are bare.

import type { Presentation, Surface } from "./flow-model";
import type { StepFactory } from "./step";

export type StepKindOptions = {
  /** Capability: the surfaces this kind can work on at all. Omitted = all. */
  surfaces?: readonly Surface[];
  /** Default presentation for nodes of this kind that do not set one. */
  presentation?: Presentation;
};

export type StepKindDef<App, E> = StepKindOptions & {
  kind: string;
  create: StepFactory<App, E>;
};

export class StepKindRegistry<App = unknown, E = unknown> {
  private readonly defs = new Map<string, StepKindDef<App, E>>();

  register(
    kind: string,
    create: StepFactory<App, E>,
    options: StepKindOptions = {},
  ): this {
    if (!kind) throw new Error("registerStepKind: kind must be non-empty");
    if (this.defs.has(kind)) {
      throw new Error(`registerStepKind: "${kind}" is already registered`);
    }
    this.defs.set(kind, { kind, create, ...options });
    return this;
  }

  has(kind: string): boolean {
    return this.defs.has(kind);
  }

  get(kind: string): StepKindDef<App, E> {
    const def = this.defs.get(kind);
    if (def == null) throw new Error(`unknown onboarding step kind "${kind}"`);
    return def;
  }

  kinds(): string[] {
    return [...this.defs.keys()];
  }

  surfacesOf = (kind: string): readonly Surface[] | null =>
    this.defs.get(kind)?.surfaces ?? null;

  /** A registry holding this one's kinds, for a product to extend without
   * mutating a shared base. */
  extend<A2 extends App = App>(): StepKindRegistry<A2, E> {
    const next = new StepKindRegistry<A2, E>();
    for (const def of this.defs.values()) {
      const { kind, create, ...options } = def;
      next.register(kind, create as StepFactory<A2, E>, options);
    }
    return next;
  }
}
